import { v4 } from "uuid";

import {
  JsInjectionModeDto,
  JsInjectionModeType,
  OnsiteInjectionDto,
} from "@intelligems/ig-types";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";
import * as rtk from "@reduxjs/toolkit";
import { createSelector, type PayloadAction } from "@reduxjs/toolkit";

import { IOnsiteInjectionsState } from "../types/onsiteInjections";

const sliceInitialOnsiteInjectionsState: IOnsiteInjectionsState = {
  isSuccess: false,
  injections: [],
  storedInjections: [],
};

const onsiteInjections = rtk.createSlice({
  name: "onsiteInjections",
  initialState: sliceInitialOnsiteInjectionsState,
  reducers: {
    resetOnsiteInjections: (draft) => {
      draft = sliceInitialOnsiteInjectionsState;
      return draft;
    },
    modClearOnsiteInjection: (draft) => {
      draft = { ...draft, injections: [] };
      return draft;
    },
    resetOfferTestOnsiteInjections: (
      draft,
      action: PayloadAction<{ variationId: string }>
    ) => {
      draft.injections = draft.injections.filter(
        (injection) => injection.variationId !== action.payload.variationId
      );
      return draft;
    },
    initOnsiteInjections: (
      draft,
      action: PayloadAction<OnsiteInjectionDto[]>
    ) => {
      draft.injections = action.payload;
      draft.storedInjections = action.payload;
    },
    initNewPersonalizationOnsiteInjections: (
      draft,
      action: PayloadAction<VariationFe>
    ) => {
      draft.injections.push({
        variationId: action.payload.id,
        customCss: "",
        customJs: "",
        jsInjectionMode: {
          type: "immediately",
        },
      });
      return draft;
    },
    setSelectedVariationJsInjectionModeType: (
      draft,
      action: PayloadAction<{
        variationId: string;
        type: JsInjectionModeType;
      }>
    ) => {
      const index = draft.injections.findIndex(
        (injection) => injection.variationId === action.payload.variationId
      );
      if (index !== -1) {
        draft.injections[index].jsInjectionMode.type = action.payload.type;
      } else {
        draft.injections.push({
          variationId: action.payload.variationId,
          jsInjectionMode: {
            type: action.payload.type,
          },
        });
      }
      return draft;
    },
    setSelectedVariationJsInjectionModeTimeout: (
      draft,
      action: PayloadAction<{
        variationId: string;
        timeout: number | string;
      }>
    ) => {
      const index = draft.injections.findIndex(
        (injection) => injection.variationId === action.payload.variationId
      );
      if (index !== -1) {
        draft.injections[index].jsInjectionMode.timeout =
          action.payload.timeout;
      } else {
        draft.injections.push({
          variationId: action.payload.variationId,
          jsInjectionMode: {
            type: "timeout",
            timeout: action.payload.timeout,
          },
        });
      }
      return draft;
    },
    setSelectedVariationInjection: (
      draft,
      action: PayloadAction<{
        variationId: string;
        type: "customCss" | "customJs";
        value: string;
        append?: boolean;
        jsInjectionMode: JsInjectionModeDto;
      }>
    ) => {
      const index = draft.injections.findIndex(
        (injection) => injection.variationId === action.payload.variationId
      );
      if (index !== -1) {
        if (action.payload.append) {
          draft.injections[index][action.payload.type] += action.payload.value;
        } else {
          draft.injections[index][action.payload.type] = action.payload.value;
        }
      } else {
        draft.injections.push({
          variationId: action.payload.variationId,
          customJs:
            action.payload.type === "customCss"
              ? undefined
              : action.payload.value,
          customCss:
            action.payload.type === "customJs"
              ? undefined
              : action.payload.value,
          jsInjectionMode: action.payload.jsInjectionMode,
        });
      }
      return draft;
    },
    setSelectedOfferVariationInjections: (
      draft,
      action: PayloadAction<{
        variationId: string;
        onsiteInjections: OnsiteInjectionDto;
      }>
    ) => {
      const index = draft.injections.findIndex(
        (injection) => injection.variationId === action.payload.variationId
      );
      const dupInjection: OnsiteInjectionDto = {
        variationId: action.payload.variationId,
        customJs: action.payload.onsiteInjections.customJs,
        customCss: action.payload.onsiteInjections.customCss,
        jsInjectionMode: action.payload.onsiteInjections.jsInjectionMode,
        id: index === -1 ? v4() : draft.injections[index].id!,
      };
      if (index !== -1) {
        draft.injections[index] = dupInjection;
      } else {
        draft.injections.push(dupInjection);
      }
      return draft;
    },
    injectionFilterByVariationId: (
      draft,
      action: PayloadAction<{
        variationId: string;
      }>
    ) => {
      draft.injections.filter(
        (injection) => injection.variationId !== action.payload.variationId
      );
      return draft;
    },
  },
  selectors: {
    selectedInjections: (state) => state.injections,
    selectedInjectionsState: (state) => state,
  },
});

export const {
  resetOnsiteInjections,
  setSelectedVariationInjection,
  initOnsiteInjections,
  initNewPersonalizationOnsiteInjections,
  modClearOnsiteInjection,
  injectionFilterByVariationId,
  setSelectedOfferVariationInjections,
  setSelectedVariationJsInjectionModeType,
  setSelectedVariationJsInjectionModeTimeout,
  resetOfferTestOnsiteInjections,
} = onsiteInjections.actions;

export const onsiteInjectionsReducer = onsiteInjections.reducer;
export const { selectedInjections, selectedInjectionsState } =
  onsiteInjections.selectors;

// Variation-specific selector for onsite injections
export const getVariationSpecificInjections = (variationId: string) =>
  createSelector(selectedInjections, (injections) =>
    injections.filter((injection) => injection.variationId === variationId)
  );
